import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import config from "../../Config";
import StudentRightsCheck from "./StudentRightsCheck";
import { getStudentPortalApiPayload } from "../../utils/studentPortalSession";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CHART_COLORS = [
  "#003970",
  "#0d6efd",
  "#198754",
  "#fd7e14",
  "#6f42c1",
  "#dc3545",
  "#20c997",
  "#6610f2",
];

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "bottom",
    },
    tooltip: {
      mode: "index",
      intersect: false,
      backgroundColor: "rgba(0, 0, 0, 0.75)",
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      title: {
        display: true,
        text: "Percentage (%)",
      },
      grid: {
        drawTicks: false,
      },
    },
    x: {
      grid: {
        drawOnChartArea: false,
      },
    },
  },
};

function ExamResultsComingSoon() {
  const [activeTab, setActiveTab] = useState("results");
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [selectedId, setSelectedId] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressRecords, setProgressRecords] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(0);

  const portalPayload = useCallback(
    (extra = {}) => getStudentPortalApiPayload(extra),
    []
  );

  const loadDetail = useCallback((resultCardId) => {
    setSelectedId(resultCardId);
    setDetail(null);
    setDetailLoading(true);

    axios({
      method: "post",
      url: config.base_url + "ResultCard/GetStudentResultDetail",
      headers: { "Content-Type": "application/json" },
      data: portalPayload({ ID: resultCardId }),
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setDetail(response.data.Result || null);
        } else if (response.data.status_message) {
          toast.error(response.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        setDetailLoading(false);
      })
      .catch(() => {
        setDetailLoading(false);
        toast.error("Could not load result detail.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  }, [portalPayload]);

  useEffect(() => {
    document.title = "Exam Results - Student Panel";
    setLoading(true);

    axios({
      method: "post",
      url: config.base_url + "ResultCard/GetStudentPublishedResults",
      headers: { "Content-Type": "application/json" },
      data: portalPayload(),
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          const list = response.data.ListofRecords || [];
          setRecords(list);
          if (list.length > 0) {
            const firstId = list[0].ResultCard_ID ?? list[0].resultCard_ID;
            if (firstId) {
              loadDetail(firstId);
            }
          }
        } else {
          setRecords([]);
          if (response.data.status_message) {
            toast.error(response.data.status_message, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Could not load exam results.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  }, [loadDetail, portalPayload]);

  useEffect(() => {
    if (activeTab !== "progress") {
      return;
    }

    setProgressLoading(true);
    axios({
      method: "post",
      url: config.base_url + "ResultCard/GetStudentSubjectProgress",
      headers: { "Content-Type": "application/json" },
      data: portalPayload({ TypeID: 0 }),
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setProgressRecords(response.data.ListofRecords || []);
        } else {
          setProgressRecords([]);
          if (response.data.status_message) {
            toast.error(response.data.status_message, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        }
        setProgressLoading(false);
      })
      .catch(() => {
        setProgressLoading(false);
        toast.error("Could not load subject progress.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  }, [activeTab, portalPayload]);

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    return Number(value).toFixed(2);
  };

  const latestResult = records.length > 0 ? records[0] : null;

  const subjectOptions = useMemo(() => {
    const map = new Map();
    progressRecords.forEach((row) => {
      if (row.Subject_ID && !map.has(row.Subject_ID)) {
        map.set(row.Subject_ID, row.Subject_Name || "Subject");
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [progressRecords]);

  const filteredProgress = useMemo(() => {
    if (selectedSubjectId <= 0) {
      return progressRecords;
    }
    return progressRecords.filter(
      (row) => Number(row.Subject_ID) === Number(selectedSubjectId)
    );
  }, [progressRecords, selectedSubjectId]);

  const subjectLineChart = useMemo(() => {
    if (selectedSubjectId <= 0 || filteredProgress.length === 0) {
      return null;
    }

    const sorted = [...filteredProgress].sort(
      (a, b) => new Date(a.Created_Date) - new Date(b.Created_Date)
    );

    return {
      labels: sorted.map(
        (row) =>
          row.Result_Title ||
          moment(row.Created_Date).format("MMM YYYY")
      ),
      datasets: [
        {
          label:
            sorted[0]?.Subject_Name ||
            subjectOptions.find((s) => s.id === selectedSubjectId)?.name ||
            "Subject",
          data: sorted.map((row) => Number(row.Percent_Marks) || 0),
          borderColor: CHART_COLORS[0],
          backgroundColor: "rgba(0, 57, 112, 0.15)",
          tension: 0.3,
          fill: true,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    };
  }, [filteredProgress, selectedSubjectId, subjectOptions]);

  const latestResultBarChart = useMemo(() => {
    if (progressRecords.length === 0) {
      return null;
    }

    const latestDate = progressRecords.reduce((max, row) => {
      const time = new Date(row.Created_Date).getTime();
      return time > max ? time : max;
    }, 0);

    const latestRows = progressRecords
      .filter((row) => new Date(row.Created_Date).getTime() === latestDate)
      .sort((a, b) => (a.Subject_Name || "").localeCompare(b.Subject_Name || ""));

    if (latestRows.length === 0) {
      return null;
    }

    return {
      labels: latestRows.map((row) => row.Subject_Name || "Subject"),
      datasets: [
        {
          label: latestRows[0]?.Result_Title || "Latest Result",
          data: latestRows.map((row) => Number(row.Percent_Marks) || 0),
          backgroundColor: CHART_COLORS.map((c) => c),
          borderWidth: 0,
          borderRadius: 6,
          barPercentage: 0.65,
        },
      ],
    };
  }, [progressRecords]);

  const multiSubjectTrendChart = useMemo(() => {
    if (selectedSubjectId > 0 || subjectOptions.length === 0) {
      return null;
    }

    const resultMap = new Map();
    progressRecords.forEach((row) => {
      const key =
        row.Result_Title || moment(row.Created_Date).format("DD MMM YYYY");
      if (!resultMap.has(key)) {
        resultMap.set(key, {
          label: key,
          date: new Date(row.Created_Date).getTime(),
        });
      }
    });

    const labels = Array.from(resultMap.values())
      .sort((a, b) => a.date - b.date)
      .map((item) => item.label);

    const datasets = subjectOptions.slice(0, 8).map((subject, index) => ({
      label: subject.name,
      data: labels.map((label) => {
        const match = progressRecords.find(
          (row) =>
            Number(row.Subject_ID) === Number(subject.id) &&
            (row.Result_Title || moment(row.Created_Date).format("DD MMM YYYY")) ===
              label
        );
        return match ? Number(match.Percent_Marks) || 0 : null;
      }),
      borderColor: CHART_COLORS[index % CHART_COLORS.length],
      backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
      tension: 0.3,
      spanGaps: true,
    }));

    return { labels, datasets };
  }, [progressRecords, selectedSubjectId, subjectOptions]);

  const sortedProgressTable = useMemo(() => {
    return [...filteredProgress].sort(
      (a, b) => new Date(b.Created_Date) - new Date(a.Created_Date)
    );
  }, [filteredProgress]);

  return (
    <>
      <StudentRightsCheck />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/student-panel/dashboard">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Exam Results
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Exam Results</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container body-content pb-4">
        {latestResult && activeTab === "results" && (
          <div className="row mb-3">
            <div className="col-md-3">
              <div className="card h-100">
                <div className="card-body py-3">
                  <small className="text-muted d-block">Latest Result</small>
                  <strong>{latestResult.Result_Title || "Result Card"}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card h-100">
                <div className="card-body py-3">
                  <small className="text-muted d-block">Percentage</small>
                  <strong>{formatNumber(latestResult.Percent_Marks)}%</strong>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card h-100">
                <div className="card-body py-3">
                  <small className="text-muted d-block">Grade</small>
                  <strong>{latestResult.Grade || "-"}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card h-100">
                <div className="card-body py-3">
                  <small className="text-muted d-block">Section Position</small>
                  <strong>{latestResult.Section_Position || "-"}</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "results" ? "active" : ""}`}
              onClick={() => setActiveTab("results")}
            >
              My Results
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "progress" ? "active" : ""}`}
              onClick={() => setActiveTab("progress")}
            >
              Subject Progress
            </button>
          </li>
        </ul>

        {activeTab === "results" && (
          <div className="row">
            <div className="col-lg-5">
              <div className="card">
                <div className="card-body">
                  <h6 className="mb-3">Published Results</h6>
                  {loading ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" role="status" />
                    </div>
                  ) : records.length === 0 ? (
                    <p className="text-muted mb-0">
                      No published results are available yet. Results appear here
                      after the school publishes your result card.
                    </p>
                  ) : (
                    <div className="list-group">
                      {records.map((item) => {
                        const cardId = item.ResultCard_ID ?? item.resultCard_ID;
                        return (
                        <button
                          key={item.ID ?? cardId}
                          type="button"
                          className={`list-group-item list-group-item-action ${
                            selectedId === cardId ? "active" : ""
                          }`}
                          onClick={() => loadDetail(cardId)}
                        >
                          <div className="fw-semibold">
                            {item.Result_Title || "Result Card"}
                          </div>
                          <small>
                            {item.Class_Name} {item.Section_Name} &middot; Grade{" "}
                            {item.Grade || "-"} &middot;{" "}
                            {formatNumber(item.Percent_Marks)}% &middot;{" "}
                            {moment(item.Created_Date).format("DD MMM YYYY")}
                          </small>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="card">
                <div className="card-body">
                  {!selectedId ? (
                    <p className="text-muted mb-0">
                      Select a result from the list to view details.
                    </p>
                  ) : detailLoading ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" role="status" />
                    </div>
                  ) : !detail ? (
                    <p className="text-muted mb-0">Result detail not found.</p>
                  ) : (
                    <>
                      <h5 className="mb-1">
                        {detail.Header?.Result_Title || "Result Detail"}
                      </h5>
                      <p className="text-muted">
                        {detail.Header?.Class_Name} {detail.Header?.Section_Name}{" "}
                        &middot; Position {detail.Header?.Section_Position || "-"}
                        &middot; Published{" "}
                        {detail.Header?.Created_Date
                          ? moment(detail.Header.Created_Date).format(
                              "DD MMM YYYY"
                            )
                          : "-"}
                      </p>
                      <div className="row mb-3">
                        <div className="col-6 col-md-3 mb-2">
                          <div className="border rounded p-2 text-center">
                            <small className="text-muted d-block">Total</small>
                            <strong>{formatNumber(detail.Header?.Total_Marks)}</strong>
                          </div>
                        </div>
                        <div className="col-6 col-md-3 mb-2">
                          <div className="border rounded p-2 text-center">
                            <small className="text-muted d-block">Obtained</small>
                            <strong>
                              {formatNumber(detail.Header?.Obtained_Marks)}
                            </strong>
                          </div>
                        </div>
                        <div className="col-6 col-md-3 mb-2">
                          <div className="border rounded p-2 text-center">
                            <small className="text-muted d-block">Percentage</small>
                            <strong>
                              {formatNumber(detail.Header?.Percent_Marks)}%
                            </strong>
                          </div>
                        </div>
                        <div className="col-6 col-md-3 mb-2">
                          <div className="border rounded p-2 text-center">
                            <small className="text-muted d-block">Grade</small>
                            <strong className="text-primary">
                              {detail.Header?.Grade || "-"}
                            </strong>
                          </div>
                        </div>
                      </div>

                      {detail.Subjects && detail.Subjects.length > 0 && (
                        <>
                          <h6>Subject Marks</h6>
                          <div className="table-responsive mb-3">
                            <table className="table table-sm table-hover">
                              <thead>
                                <tr>
                                  <th>Subject</th>
                                  <th>Obtained</th>
                                  <th>Total</th>
                                  <th>%</th>
                                  <th>Grade</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.Subjects.map((sub, index) => (
                                  <tr key={index}>
                                    <td>{sub.Subject_Name}</td>
                                    <td>{formatNumber(sub.Obtained_Marks)}</td>
                                    <td>{formatNumber(sub.Total_Marks)}</td>
                                    <td>{formatNumber(sub.Percent_Marks)}</td>
                                    <td>{sub.Grade || "-"}</td>
                                    <td>
                                      {sub.Is_Pass === true && (
                                        <span className="badge bg-success">Pass</span>
                                      )}
                                      {sub.Is_Pass === false && (
                                        <span className="badge bg-danger">Fail</span>
                                      )}
                                      {sub.Is_Pass !== true && sub.Is_Pass !== false && (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}

                      {detail.Breakdown && detail.Breakdown.length > 0 && (
                        <>
                          <h6>Exam Breakdown</h6>
                          <p className="text-muted small">
                            How each source exam contributed to this composite result.
                          </p>
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead>
                                <tr>
                                  <th>Exam</th>
                                  <th>Weight %</th>
                                  <th>Obtained</th>
                                  <th>Total</th>
                                  <th>Exam %</th>
                                  <th>Weighted</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.Breakdown.map((row, index) => (
                                  <tr key={index}>
                                    <td>{row.Exam_Title}</td>
                                    <td>{formatNumber(row.Weightage_Percent)}</td>
                                    <td>{formatNumber(row.Obtained_Marks)}</td>
                                    <td>{formatNumber(row.Total_Marks)}</td>
                                    <td>{formatNumber(row.Exam_Percent)}</td>
                                    <td>{formatNumber(row.Weighted_Score)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "progress" && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label mb-1">Filter by Subject</label>
                <select
                  className="form-select"
                  value={selectedSubjectId}
                  onChange={(e) => setSelectedSubjectId(Number(e.target.value))}
                >
                  <option value={0}>All Subjects — overview</option>
                  {subjectOptions.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {progressLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" />
              </div>
            ) : progressRecords.length === 0 ? (
              <div className="card">
                <div className="card-body">
                  <p className="text-muted mb-0">
                    No subject progress data yet. Progress charts appear after at
                    least one result card has been published for you.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="row mb-3">
                  {selectedSubjectId > 0 && subjectLineChart && (
                    <div className="col-lg-7 mb-3">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="mb-3">Subject Trend Over Published Results</h6>
                          <div style={{ height: "320px" }}>
                            <Line data={subjectLineChart} options={chartOptions} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSubjectId <= 0 && latestResultBarChart && (
                    <div className="col-lg-6 mb-3">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="mb-3">Latest Result — Subject Performance</h6>
                          <div style={{ height: "320px" }}>
                            <Bar
                              data={latestResultBarChart}
                              options={{
                                ...chartOptions,
                                plugins: {
                                  ...chartOptions.plugins,
                                  legend: { display: false },
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedSubjectId <= 0 && multiSubjectTrendChart && (
                    <div className="col-lg-6 mb-3">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="mb-3">Progress Across Result Cards</h6>
                          <div style={{ height: "320px" }}>
                            <Line
                              data={multiSubjectTrendChart}
                              options={chartOptions}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="card">
                  <div className="card-body">
                    <h6 className="mb-3">Subject History</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Result Card</th>
                            <th>Subject</th>
                            <th>Obtained</th>
                            <th>Total</th>
                            <th>%</th>
                            <th>Grade</th>
                            <th>Published</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedProgressTable.map((row, index) => (
                            <tr key={index}>
                              <td>{row.Result_Title || "-"}</td>
                              <td>{row.Subject_Name || "-"}</td>
                              <td>{formatNumber(row.Obtained_Marks)}</td>
                              <td>{formatNumber(row.Total_Marks)}</td>
                              <td>{formatNumber(row.Percent_Marks)}</td>
                              <td>{row.Grade || "-"}</td>
                              <td>
                                {row.Created_Date
                                  ? moment(row.Created_Date).format("DD MMM YYYY")
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default ExamResultsComingSoon;
